var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { assertNever } from "@contember/utilities";
import { ChildrenAnalyzerError } from "./ChildrenAnalyzerError.js";
import { wrapError } from "./helpers/wrapError.js";
import { getErrorMessage } from "./helpers/getErrorMessage.js";
const _ChildrenAnalyzer = class {
  constructor(leaves, decider = [], options = {}) {
    __publicField(this, "leaves");
    __publicField(this, "branchNodes");
    __publicField(this, "options");
    this.leaves = leaves;
    if (Array.isArray(decider)) {
      this.branchNodes = decider;
    } else {
      this.branchNodes = [];
      options = decider;
    }
    this.options = { ..._ChildrenAnalyzer.defaultOptions, ...options };
  }
  processChildren(children, initialStaticContext) {
    const processed = this.processNode(children, initialStaticContext, []);
    const rawResult = Array.isArray(
      processed
    ) ? processed : [processed];
    return rawResult.filter(
      (item) => item !== void 0
    );
  }
  processNode(node, staticContext, componentPath) {
    if (!node || typeof node === "string" || typeof node === "number" || typeof node === "boolean") {
      for (const leaf of this.leaves) {
        const specification = leaf.specification;
        if (specification.type === "useSite") {
          const { ComponentType, factory } = specification;
          if (ComponentType === void 0) {
            return factory(node, staticContext);
          }
        }
      }
      if (node === false || node === void 0 || node === null) {
        return void 0;
      }
      if (!this.options.ignoreUnhandledNodes) {
        throw new ChildrenAnalyzerError(getErrorMessage(this.options.unhandledNodeErrorMessage, node, staticContext));
      }
      return void 0;
    }
    if (typeof node === "function") {
      if (this.options.ignoreRenderProps) {
        return void 0;
      }
      throw new ChildrenAnalyzerError(getErrorMessage(this.options.renderPropsErrorMessage, node, staticContext));
    }
    if (Array.isArray(node)) {
      let mapped = [];
      for (const subNode of node) {
        const processed = this.processNode(subNode, staticContext, componentPath);
        if (processed !== void 0) {
          if (Array.isArray(processed)) {
            mapped = mapped.concat(processed);
          } else {
            mapped.push(processed);
          }
        }
      }
      return mapped;
    }
    let children = void 0;
    if (!("type" in node)) {
      return this.processNode(children, staticContext, componentPath);
    }
    children = node.props.children;
    if (typeof node.type === "symbol") {
      return this.processNode(children, staticContext, componentPath);
    }
    const treeNode = node.type;
    componentPath = [...componentPath, node];
    if (typeof treeNode !== "string") {
      if (this.options.staticContextFactoryName in treeNode) {
        const staticContextFactory = treeNode[this.options.staticContextFactoryName];
        try {
          staticContext = staticContextFactory(node.props, staticContext);
        } catch (e) {
          wrapError(e, treeNode.displayName ?? "???", this.options.staticContextFactoryName, componentPath);
        }
      }
      if (this.options.staticRenderFactoryName in treeNode) {
        const factory = treeNode[this.options.staticRenderFactoryName];
        try {
          children = factory(node.props, staticContext);
        } catch (e) {
          wrapError(e, treeNode.displayName ?? "???", this.options.staticRenderFactoryName, componentPath);
        }
      }
    }
    for (const leaf of this.leaves) {
      const specification = leaf.specification;
      switch (specification.type) {
        case "declarationSite": {
          const { factoryMethodName } = specification;
          if (typeof treeNode !== "string" && factoryMethodName in treeNode) {
            const factory = treeNode[factoryMethodName];
            return factory(node.props, staticContext);
          }
          break;
        }
        case "useSite": {
          const { ComponentType, factory } = specification;
          if (ComponentType === void 0 || node.type === ComponentType) {
            return factory(node, staticContext);
          }
          break;
        }
        default:
          return assertNever(specification);
      }
    }
    const processedChildren = this.processNode(children, staticContext, componentPath);
    for (const branchNode of this.branchNodes) {
      const specification = branchNode.specification;
      switch (specification.type) {
        case "declarationSite": {
          const { factoryMethodName, childrenRepresentationReducer } = specification;
          if (typeof treeNode !== "string" && factoryMethodName in treeNode) {
            if (processedChildren === void 0 && !branchNode.options.childrenAreOptional) {
              throw new ChildrenAnalyzerError(branchNode.options.childrenAbsentErrorMessage);
            }
            const factory = treeNode[factoryMethodName];
            return factory(node.props, childrenRepresentationReducer(processedChildren), staticContext);
          }
          break;
        }
        case "useSite": {
          const { factory, ComponentType } = specification;
          if (ComponentType === void 0 || node.type === ComponentType) {
            if (processedChildren === void 0 && !branchNode.options.childrenAreOptional) {
              throw new ChildrenAnalyzerError(branchNode.options.childrenAbsentErrorMessage);
            }
            return factory(node, processedChildren, staticContext);
          }
          break;
        }
        default:
          return assertNever(specification);
      }
    }
    if (!this.options.ignoreUnhandledNodes && !(typeof treeNode !== "string" && this.options.staticRenderFactoryName in treeNode)) {
      throw new ChildrenAnalyzerError(getErrorMessage(this.options.unhandledNodeErrorMessage, node, staticContext));
    }
    return processedChildren;
  }
};
let ChildrenAnalyzer = _ChildrenAnalyzer;
__publicField(ChildrenAnalyzer, "defaultOptions", {
  ignoreRenderProps: true,
  renderPropsErrorMessage: "Render props (functions as React component children) are not supported.",
  ignoreUnhandledNodes: true,
  unhandledNodeErrorMessage: "Encountered an unknown child.",
  staticContextFactoryName: "getStaticContext",
  staticRenderFactoryName: "staticRender"
});
export {
  ChildrenAnalyzer
};
//# sourceMappingURL=ChildrenAnalyzer.js.map
